import React, { Component } from 'react';

var config = process.env;

const TIPO_LABEL = {
    cargo: 'Consumo',
    pago: 'Pago',
    reversa: 'Venta anulada',
    ajuste: 'Ajuste',
};

const MEDIO_LABEL = { 1: 'efectivo', 2: 'transferencia' };

/**
 * Estado de cuenta de un empleado, en su ficha (backend/docs/spec-cuenta-empleados.md FR-090,
 * FR-095, FR-096).
 *
 * No es por donde se opera —eso es el panel de la pantalla de Caja— sino a donde se viene cuando
 * alguien dice «yo eso lo pagué»: el detalle movimiento por movimiento, con el saldo acumulado,
 * quién cargó cada asiento y si el pago se encontró en el extracto.
 *
 * La cuenta bancaria se edita acá porque es el dato que hace mecánico el cruce mensual: las
 * transferencias de otro banco llegan al extracto sin nombre y sin cuenta.
 */
class EmployeeAccountCard extends Component {

    state = { balance: 0, movements: [], pendingConfirmation: 0, bankAccount: '', cargando: true, guardando: false, error: null };

    componentDidMount() {
        this.cargar();
    }

    money = (n) => `$${Number(n || 0).toLocaleString('de-DE')}`;

    // OJO con `transfer_date`: es un DATEONLY y llega como 'YYYY-MM-DD'. `new Date('2026-09-10')`
    // lo parsea como MEDIANOCHE UTC, y formatearlo en UTC-3 lo corre al DÍA ANTERIOR — la fecha
    // que el cajero eligió deja de coincidir con la que ve, que es justo el dato con el que
    // después se busca la transferencia en el extracto. Una fecha sin hora se formatea tal cual,
    // sin pasar por Date; los timestamps completos sí se convierten.
    fecha = (valor) => {
        if (!valor) return '';
        const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(valor));
        if (soloFecha) {
            const [, anio, mes, dia] = soloFecha;
            return `${dia}/${mes}/${anio.slice(2)}`;
        }
        try {
            return new Intl.DateTimeFormat('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(new Date(valor));
        } catch (e) {
            return '';
        }
    };

    cargar = () => {
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}employeeAccount/${encodeURIComponent(this.props.cedula)}`, {
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        })
            .then((r) => r.json())
            .then((data) => {
                if (data && data.error === 0 && data.data) {
                    this.setState({
                        balance: data.data.balance || 0,
                        movements: data.data.movements || [],
                        pendingConfirmation: data.data.pendingConfirmation || 0,
                        bankAccount: data.data.bankAccount || '',
                        cargando: false,
                    });
                    return;
                }
                this.setState({ cargando: false, error: (data && data.message) || 'No se pudo cargar la cuenta.' });
            })
            .catch(() => this.setState({ cargando: false, error: 'No se pudo cargar la cuenta.' }));
    };

    guardarCuentaBancaria = () => {
        this.setState({ guardando: true });
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}clients/${encodeURIComponent(this.props.cedula)}/bank-account`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
            body: JSON.stringify({ bankAccount: this.state.bankAccount }),
        })
            .then((r) => r.json())
            .then((data) => this.setState({
                guardando: false,
                error: data && data.error === 0 ? null : ((data && data.message) || 'No se pudo guardar la cuenta bancaria.'),
            }))
            .catch(() => this.setState({ guardando: false, error: 'No se pudo guardar la cuenta bancaria.' }));
    };

    // Marcar / desmarcar un pago contra el extracto. Desmarcar conserva quién había confirmado:
    // el rastro tiene que sobrevivir a un tilde puesto por error.
    alternarConfirmacion = (mov) => {
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}employeeAccount/movement/${mov.id}/confirmation`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
            body: JSON.stringify({ confirmed: !mov.confirmed }),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data && data.error === 0) { this.cargar(); return; }
                this.setState({ error: (data && data.message) || 'No se pudo cambiar la confirmación.' });
            })
            .catch(() => this.setState({ error: 'No se pudo cambiar la confirmación.' }));
    };

    esTransferencia = (m) => m.type === 'pago' && Number(m.payment_type) === 2;

    render() {
        const { balance, movements, pendingConfirmation, cargando, error } = this.state;

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="m-0">Cuenta del equipo</h6>
                    <span className={balance > 0 ? 'text-danger font-weight-bold' : 'text-success'}>
                        {balance > 0
                            ? `Debe ${this.money(balance)}`
                            : (balance < 0 ? `${this.money(-balance)} a favor` : 'Al día')}
                    </span>
                </div>

                <div className="card-body">
                    {error && <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 13 }}>{error}</div>}

                    <div className="form-group">
                        <label className="d-block" style={{ fontSize: 13 }}>
                            Cuenta bancaria desde la que transfiere
                            <span className="text-muted"> — es lo que la encuentra en el extracto</span>
                        </label>
                        <div className="input-group input-group-sm">
                            <input
                                type="text" className="form-control form-control-sm" maxLength={60}
                                placeholder="Banco y número de cuenta"
                                value={this.state.bankAccount}
                                onChange={(e) => this.setState({ bankAccount: e.target.value, error: null })}
                            />
                            <div className="input-group-append">
                                <button className="btn btn-white" onClick={this.guardarCuentaBancaria} disabled={this.state.guardando}>
                                    {this.state.guardando ? '…' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {pendingConfirmation > 0 && (
                        <small className="text-muted d-block mb-2">
                            {pendingConfirmation} transferencia{pendingConfirmation === 1 ? '' : 's'} sin confirmar contra el extracto.
                        </small>
                    )}

                    {cargando && <small className="text-muted">Cargando…</small>}

                    {!cargando && movements.length === 0 && (
                        <small className="text-muted">Todavía no hay movimientos.</small>
                    )}

                    {movements.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Concepto</th>
                                        <th className="text-right">Importe</th>
                                        <th className="text-right">Saldo</th>
                                        <th>Quién</th>
                                        <th>Extracto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {movements.map((m) => (
                                        <tr key={m.id}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{this.fecha(m.created_at)}</td>
                                            <td>
                                                {TIPO_LABEL[m.type] || m.type}
                                                {m.payment_type ? ` · ${MEDIO_LABEL[m.payment_type] || m.payment_type}` : ''}
                                                {m.transfer_date ? ` · transferida el ${this.fecha(m.transfer_date)}` : ''}
                                                {m.notes ? <span className="text-muted"> · {m.notes}</span> : null}
                                                {m.reference ? <span className="text-muted"> · {m.reference}</span> : null}
                                            </td>
                                            <td className="text-right" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                {this.money(m.amount)}
                                            </td>
                                            <td className="text-right" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                {this.money(m.runningBalance)}
                                            </td>
                                            <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{m.created_by_name || '—'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {/* Sólo las transferencias están en el extracto: un pago en
                                                    efectivo no puede confirmarse ahí. */}
                                                {this.esTransferencia(m) ? (
                                                    <button
                                                        className={`btn btn-sm ${m.confirmed ? 'btn-success' : 'btn-white'}`}
                                                        style={{ padding: '0 .4rem', fontSize: 12 }}
                                                        onClick={() => this.alternarConfirmacion(m)}
                                                    >
                                                        {m.confirmed ? 'Confirmada' : 'Confirmar'}
                                                    </button>
                                                ) : <span className="text-muted">—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default EmployeeAccountCard;
